import { AIR, dcos, dsin } from '@vm/shared';
import { M } from '../palette';
import { brushAt, MapBuilder, speckle, stripes, yawToward, type Brush } from './builder';
import { stairKeepClear,
  awning, boundaryBand, bunting, busStop, cabinet, ceilingLight, dumpster, doorsFor, freeGround, G, house, lampPost, lemonTree, palmTree, parasol, pieces, Region, scatter, shopSign, TRIM,
  barrier, ceilingStrip, type Box, type Footprint, type Piece,
  regionPlay,
} from './kit';
import { emptyMeta, type MapDef, type MapMeta } from './types';

/**
 * Seaside Arcade: a Mediterranean seaside town in the layout of a famous city-block arena map (see docs/maps/reference-maps.md): a main street
 * crossing a central square, shops and an arcade hall along both sides, a hotel at the north end, an open lane on the east, and a yard at the
 * south-west tip. The reference plan is drawn on two grids 25 degrees apart; it is turned so its main grid is axis-aligned (the plan keeps its
 * slanted outline, and the few slanted buildings are built square, as near the original footprint as the boundary allows). The plan is
 * the reference's (66 x 75 m play area); everything you see is ours: aqua, coral, sun and navy stucco, striped awnings, shop signs, palms, a
 * fountain plaza, an arcade full of cabinets, a boardwalk lane and a Ferris wheel over the sea wall.
 * Not mirrored: the plan is asymmetric, so both teams' spawns are placed by hand and checked with tools/map-audit.ts.
 */
const SX = 896;
const SY = 224;
const SZ = 896;
const OX = 115; // play-area origin (voxels)
const OZ = 72;
const px = (x: number): number => OX + Math.round(x * 10);
const pz = (z: number): number => OZ + Math.round(z * 10);
const wx = (x: number): number => OX / 10 + x; // play metres -> world metres
const wz = (z: number): number => OZ / 10 + z;
const WALL_H = 58;

const OUTLINE: [number, number][] = [
  [59.4, 7.7], [55.4, 9.6], [52.9, 4.9], [47.7, 7.4], [44.8, 1.9], [40.9, 3.6], [38.8, 0.0], [27.1, 5.7], [33.2, 19.0], [35.0, 18.5], [36.2, 21.5], [6.4, 17.3],
  [2.5, 37.6], [0.2, 42.3], [0.0, 45.6], [0.6, 57.8], [13.0, 73.0], [16.9, 75.1], [26.7, 65.5], [28.9, 54.6], [64.7, 58.3], [66.5, 41.7], [66.5, 33.9],
  [64.7, 24.4], [66.6, 22.8], [64.7, 17.0], [63.1, 12.2], [61.7, 12.3],
];
const region = new Region(OUTLINE);

// Palette ---------------------------------------------------------------------------------------
const AQUA = M('stucco_aqua');
const CORAL = M('stucco_coral');
const SUN = M('stucco_sun');
const NAVY = M('stucco_navy');
const WHITE = M('plaster_3');
const TERRA = M('roof_red_1');
const SAND: Brush = speckle([M('sand_1'), M('sand_3'), M('sand_2')], 51);
const PAVE: Brush = speckle([M('concrete_1'), M('concrete_3'), M('concrete_1'), M('sand_3')], 53);
const PLAZA: Brush = (x, _y, z) => (((x >> 3) + (z >> 3)) % 2 === 0 ? M('tile_white') : AQUA);
const BOARD: Brush = stripes(M('planks_1'), M('planks_3'), 4, 'z');
const LIGHTS = [M('neon_pink'), M('neon_cyan'), M('neon_yellow'), M('neon_green'), M('neon_orange')];
const FLAGS = [M('paint_pink'), M('paint_cyan'), M('paint_yellow'), M('paint_lime'), M('paint_orange')];

// Buildings: footprints in play metres from the reference plan --------------------------------------------------
type Kind = 'hotel' | 'cafe' | 'shop' | 'arcade' | 'flat' | 'tower';
interface Bld extends Footprint {
  name: string;
  floors: number;
  wall: number;
  roof: number;
  roofStyle: 'flat' | 'gable' | 'gableX' | 'pyramid';
  kind: Kind;
  sign: string;
  signBg: number;
  signFg: number;
}
const BUILDINGS: Bld[] = [
  { name: 'hotel', x0: 31.5, z0: 5.6, x1: 39.5, z1: 12.5, floors: 3, wall: SUN, roof: TERRA, roofStyle: 'flat', kind: 'hotel', sign: 'HOTEL', signBg: NAVY, signFg: M('neon_yellow') },
  { name: 'icehall', x0: 34.5, z0: 15.5, x1: 40.7, z1: 26.5, floors: 2, wall: CORAL, roof: TERRA, roofStyle: 'gable', kind: 'cafe', sign: 'ICE', signBg: AQUA, signFg: M('neon_pink') },
  { name: 'surf', x0: 34.5, z0: 27.0, x1: 42.0, z1: 33.0, floors: 1, wall: AQUA, roof: TERRA, roofStyle: 'flat', kind: 'shop', sign: 'SURF', signBg: NAVY, signFg: M('neon_cyan') },
  { name: 'booth', x0: 42.5, z0: 26.0, x1: 46.5, z1: 31.0, floors: 1, wall: SUN, roof: TERRA, roofStyle: 'gable', kind: 'shop', sign: 'TIX', signBg: CORAL, signFg: M('paint_white') },
  { name: 'row', x0: 56.5, z0: 13.0, x1: 62.5, z1: 23.0, floors: 2, wall: AQUA, roof: TERRA, roofStyle: 'gableX', kind: 'flat', sign: 'INN', signBg: NAVY, signFg: M('neon_green') },
  { name: 'grill', x0: 58.0, z0: 27.0, x1: 64.5, z1: 34.0, floors: 2, wall: CORAL, roof: TERRA, roofStyle: 'gable', kind: 'cafe', sign: 'GRILL', signBg: NAVY, signFg: M('neon_orange') },
  { name: 'arcade', x0: 47.0, z0: 42.0, x1: 60.0, z1: 54.0, floors: 2, wall: NAVY, roof: TERRA, roofStyle: 'flat', kind: 'arcade', sign: 'ARCADE', signBg: M('paint_black'), signFg: M('neon_pink') },
  { name: 'belltower', x0: 34.5, z0: 40.0, x1: 39.5, z1: 47.5, floors: 2, wall: WHITE, roof: M('roof_blue'), roofStyle: 'pyramid', kind: 'tower', sign: 'BELL', signBg: NAVY, signFg: M('neon_yellow') },
  { name: 'market', x0: 20.0, z0: 38.0, x1: 29.5, z1: 45.0, floors: 2, wall: SUN, roof: TERRA, roofStyle: 'gableX', kind: 'shop', sign: 'FISH', signBg: AQUA, signFg: M('paint_white') },
  { name: 'pierstore', x0: 21.5, z0: 47.5, x1: 26.5, z1: 64.0, floors: 1, wall: WHITE, roof: TERRA, roofStyle: 'gableX', kind: 'shop', sign: 'PIER', signBg: NAVY, signFg: M('neon_cyan') },
  { name: 'guesthouse', x0: 5.0, z0: 27.5, x1: 12.5, z1: 41.5, floors: 2, wall: CORAL, roof: TERRA, roofStyle: 'gableX', kind: 'flat', sign: 'ROOMS', signBg: AQUA, signFg: M('paint_white') },
  { name: 'beachbar', x0: 3.0, z0: 43.5, x1: 10.5, z1: 54.5, floors: 1, wall: AQUA, roof: TERRA, roofStyle: 'flat', kind: 'cafe', sign: 'BAR', signBg: CORAL, signFg: M('neon_yellow') },
  { name: 'kiosk', x0: 16.0, z0: 66.5, x1: 20.0, z1: 70.5, floors: 1, wall: SUN, roof: TERRA, roofStyle: 'pyramid', kind: 'shop', sign: 'ICE', signBg: NAVY, signFg: M('neon_pink') },
  { name: 'bakery', x0: 7.5, z0: 19.0, x1: 14.0, z1: 25.0, floors: 2, wall: SUN, roof: TERRA, roofStyle: 'gable', kind: 'cafe', sign: 'BUNS', signBg: CORAL, signFg: M('paint_white') },
  { name: 'boats', x0: 21.5, z0: 21.5, x1: 30.0, z1: 27.5, floors: 1, wall: NAVY, roof: TERRA, roofStyle: 'flat', kind: 'shop', sign: 'BOATS', signBg: AQUA, signFg: M('paint_white') },
  { name: 'lifeguard', x0: 42.0, z0: 3.6, x1: 45.0, z1: 8.6, floors: 1, wall: CORAL, roof: TERRA, roofStyle: 'pyramid', kind: 'shop', sign: 'SAFE', signBg: NAVY, signFg: M('paint_white') },
];

const place = (x: number, z: number, r: number, fn: () => void): void => {
  if (region.inside(x, z) && region.edgeDistance(x, z) > r) fn();
};

/** Colour cabinets for the arcade. */
const CAB = [M('neon_pink'), M('neon_cyan'), M('neon_yellow'), M('neon_green'), M('neon_orange'), M('neon_purple')];

function furnish(b: MapBuilder, bd: Bld, box: Box, seed: number): void {
  const boxOf = box;
  for (let f = 0; f < bd.floors; f++) {
    const y = G + 30 * f;
    const room = { x0: box.x0 + 2, z0: box.z0 + 2, x1: box.x1 - 2, z1: box.z1 - 2, y };
    const avoid = stairKeepClear(boxOf, f);
    const up = f > 0;
    const lots: Piece[] = [];
    switch (bd.kind) {
      case 'hotel':
        if (!up) lots.push(pieces.counter(b, y, 50, 7), pieces.sofa(b, y, M('cloth_red')), pieces.sofa(b, y, M('cloth_blue')), pieces.table(b, y, 18, 10), pieces.shelfX(b, y, 24, 22));
        else lots.push(pieces.bed(b, y, M('cloth_blue')), pieces.bed(b, y, M('cloth_red')), pieces.bed(b, y, M('cloth_yellow')), pieces.shelfX(b, y, 22, 20), pieces.table(b, y, 14, 10));
        break;
      case 'cafe':
        lots.push(pieces.counter(b, y, 44, 7), pieces.table(b, y, 20, 12), pieces.table(b, y, 20, 12), pieces.table(b, y, 16, 10), pieces.shelfX(b, y, 22, 22), pieces.barrel(b, y, M('paint_orange')));
        break;
      case 'shop':
        lots.push(pieces.shelfX(b, y, 26, 22), pieces.counter(b, y, 36, 7), pieces.shelfX(b, y, 24, 22), pieces.crate(b, y, 10), pieces.crate(b, y, 8), pieces.barrel(b, y, M('paint_teal')));
        break;
      case 'arcade':
        for (let i = 0; i < 12; i++) lots.push({ w: 9, d: 7, draw: (x, z) => cabinet(b, x, y, z, CAB[(i + seed) % CAB.length]) });
        lots.push(pieces.counter(b, y, 40, 7), pieces.table(b, y, 20, 12), pieces.crate(b, y, 10));
        break;
      case 'flat':
        if (!up) lots.push(pieces.sofa(b, y, M('cloth_blue')), pieces.table(b, y, 20, 12), pieces.counter(b, y, 40, 7), pieces.shelfX(b, y, 20, 22));
        else lots.push(pieces.bed(b, y, M('cloth_red')), pieces.bed(b, y, M('cloth_green')), pieces.shelfX(b, y, 20, 20), pieces.table(b, y, 14, 10));
        break;
      case 'tower':
        lots.push(pieces.crate(b, y, 10), pieces.barrel(b, y, M('paint_blue')), pieces.crate(b, y, 8));
        break;
    }
    scatter(b, room, lots, seed + f * 3, avoid);
    // Ceiling lights down the middle of the room, away from the stairs' head room.
    ceilingStrip(b, room, G + 27 + 30 * f + (f === bd.floors - 1 ? 1 : 0), LIGHTS, avoid, 26, f);
  }
}

function build(b: MapBuilder): MapMeta {
  // Ground, paving and the sea wall -----------------------------------------------------------------------------------------
  b.ground(G, SAND, speckle([M('sandstone_1'), M('sandstone_3')], 52), speckle([M('rock_sand'), M('rock_1')], 54));
  boundaryBand(b, region, { ox: OX, oz: OZ, band: 4, height: WALL_H, skirt: NAVY, skirtH: 9, face: WHITE, core: M('rock_1'), cap: M('clay') });
  const bad: string[] = [];
  for (const bd of BUILDINGS)
    for (const [x, z] of [[bd.x0, bd.z0], [bd.x1, bd.z0], [bd.x0, bd.z1], [bd.x1, bd.z1]])
      if (!region.inside(x, z) || region.edgeDistance(x, z) < 0.15) bad.push(`${bd.name} (${x}, ${z})`);
  if (bad.length) throw new Error(`seaside_arcade: corners outside or on the boundary: ${bad.join('; ')}`);
  // Overlapping footprints would merge two buildings.
  for (const a of BUILDINGS) for (const c of BUILDINGS) if (a.name < c.name && a.x0 < c.x1 + 0.3 && a.x1 > c.x0 - 0.3 && a.z0 < c.z1 + 0.3 && a.z1 > c.z0 - 0.3) throw new Error(`seaside_arcade: ${a.name} and ${c.name} touch`);

  const plazaC: [number, number] = [26.0, 32.5];
  for (let X = px(0); X < px(67); X++)
    for (let Z = pz(0); Z < pz(76); Z++) {
      const xm = (X - OX + 0.5) / 10;
      const zm = (Z - OZ + 0.5) / 10;
      if (!region.inside(xm, zm)) continue;
      if (Math.hypot(xm - plazaC[0], zm - plazaC[1]) < 8.5) b.set(X, G - 1, Z, brushAt(PLAZA, X, G - 1, Z));
      else if (xm > 41.5 && xm < 52.5 && zm > 8 && zm < 28) b.set(X, G - 1, Z, brushAt(BOARD, X, G - 1, Z));
      else b.set(X, G - 1, Z, brushAt(PAVE, X, G - 1, Z));
    }

  // Buildings ---------------------------------------------------------------------------------------------------------------------
  const boxes = new Map<string, Box>();
  const sideOf = new Map<string, ReturnType<typeof doorsFor>>();
  for (const bd of BUILDINGS) {
    const doors = doorsFor(bd, BUILDINGS, region, 'seaside_arcade');
    sideOf.set(bd.name, doors);
    const box = house(b, {
      x0: px(bd.x0), z0: pz(bd.z0), x1: px(bd.x1), z1: pz(bd.z1), floors: bd.floors, wall: bd.wall, roof: bd.roof, roofStyle: bd.roofStyle, doors,
      post: TRIM, trim: TRIM, plinth: M('stone_3'), floor: M('tile_white'), windows: { w: 10, h: 13, sill: 8, spacing: 22 },
    });
    boxes.set(bd.name, box);
  }
  let seed = 5;
  for (const bd of BUILDINGS) furnish(b, bd, boxes.get(bd.name)!, (seed += 7));
  // Signs and awnings over the first door of each shop.
  const awnColors = [M('paint_pink'), M('paint_cyan'), M('paint_yellow'), M('paint_orange'), M('paint_lime')];
  let ai = 0;
  for (const bd of BUILDINGS) {
    const box = boxes.get(bd.name)!;
    const d = sideOf.get(bd.name)![0];
    shopSign(b, box, d.side, bd.sign, bd.signBg, bd.signFg);
    if (bd.floors === 1 || bd.kind === 'cafe' || bd.kind === 'shop') {
      const mid = d.side === 'n' || d.side === 's' ? (box.x0 + box.x1) >> 1 : (box.z0 + box.z1) >> 1;
      const run = d.side === 'n' || d.side === 's' ? box.x1 - box.x0 : box.z1 - box.z0;
      if (run >= 40) awning(b, d.side, box, mid - 8, 16, awnColors[ai++ % awnColors.length]);
    }
  }
  // The arcade's roof sign: big neon letters above the parapet on its west face.
  {
    const bx = boxes.get('arcade')!;
    const top = G + 60 + 4;
    b.box(bx.x0 + 4, top, bx.z0 + 6, bx.x1 - 4, top + 1, bx.z0 + 8, M('paint_black'));
    for (let x = bx.x0 + 6; x < bx.x1 - 6; x += 6) b.box(x, top + 1, bx.z0 + 6, x + 2, top + 9, bx.z0 + 8, LIGHTS[((x - bx.x0) / 6) % LIGHTS.length | 0]);
  }

  // The plaza and the streets ---------------------------------------------------------------------------------------------------
  b.fountain(px(plazaC[0]), G, pz(plazaC[1]), 22);
  for (const [x, z, c] of [[19.0, 29.6, 0], [32.5, 29.6, 1], [19.0, 35.8, 2], [32.8, 35.6, 3]] as [number, number, number][]) place(x, z, 1.5, () => parasol(b, px(x), pz(z), FLAGS[c]));
  for (const [x, z] of [[17.0, 31.0], [33.0, 34.0], [14.5, 31.0], [36.5, 36.5], [45.0, 22.0], [49.5, 12.0], [14.0, 59.5], [11.0, 66.0], [30.0, 49.0], [44.0, 47.0], [55.0, 37.0]] as [number, number][])
    place(x, z, 2.0, () => {
      if (freeGround(b, px(x) - 3, pz(z) - 3, px(x) + 4, pz(z) + 4)) palmTree(b, px(x), pz(z), 48 + ((x * 7 + z * 3) % 12), x < 33 ? 'e' : 'w');
    });
  for (const [x, z] of [[16.0, 24.5], [18.5, 20.5]] as [number, number][]) place(x, z, 1.5, () => lemonTree(b, px(x), pz(z), 34));
  // The courtyard north-west of the plaza: planters, lemon trees and a low wall.
  place(16.0, 23.0, 2.0, () => {
    b.box(px(14.5), G, pz(21.5), px(19.5), G + 5, pz(22.0), M('stone_3'));
    b.box(px(14.5), G, pz(26.0), px(19.5), G + 5, pz(26.5), M('stone_3'));
  });
  // Bunting across the central street and the lane.
  bunting(b, px(13.5), G + 44, pz(33.0), px(38.0), G + 44, pz(30.5), FLAGS, 7);
  bunting(b, px(17.0), G + 44, pz(36.5), px(31.0), G + 44, pz(36.8), FLAGS, 5);
  bunting(b, px(47.0), G + 40, pz(10.0), px(53.0), G + 40, pz(24.0), FLAGS, 6);
  // Street furniture and cover.
  for (const [x, z, c] of [[14.0, 28.5, 2], [27.0, 37.0, 0], [41.0, 12.0, 3], [50.0, 25.0, 1], [8.0, 56.5, 0], [34.0, 57.0, 2]] as [number, number, number][])
    place(x, z, 1.2, () => lampPost(b, px(x), pz(z), LIGHTS[c]));
  place(21.5, 33.5, 1.5, () => dumpster(b, px(14.5), pz(25.6), true));
  place(47.0, 38.0, 1.5, () => dumpster(b, px(46.0), pz(34.6), false));
  place(30.0, 46.0, 1.5, () => dumpster(b, px(29.8), pz(46.5), true));
  place(7.0, 61.0, 1.5, () => busStop(b, px(3.0), pz(59.6), M('neon_cyan')));
  for (const [x, z, len, alongX] of [[44.0, 33.8, 24, true], [47.0, 21.8, 20, true], [16.5, 29.6, 24, true], [30.2, 29.4, 20, true], [13.0, 56.2, 24, true], [37.0, 49.8, 24, true]] as [number, number, number, boolean][])
    place(x + len / 20, z, 1.0, () => barrier(b, px(x), pz(z), len, alongX));
  // Crates and stacked boxes in the lane and the south yard.
  for (const [x, z] of [[44.8, 16.5], [43.2, 20.6], [50.8, 18.2], [9.0, 61.0], [17.5, 58.6], [12.6, 56.8], [49.0, 39.6], [43.0, 36.0]] as [number, number][])
    place(x, z, 1.0, () => {
      if (freeGround(b, px(x), pz(z), px(x) + 10, pz(z) + 10)) {
        b.crate(px(x), G, pz(z), 10);
        if ((x * 3 + z) % 2 < 1) b.crate(px(x) + 1, G + 10, pz(z) + 1, 8);
      }
    });

  // The Ferris wheel over the east wall: a landmark you can see from every street ----------------------------------------------------
  {
    const cx = px(73.0);
    const cy = G + 80;
    const cz = pz(39.0);
    const R = 52;
    const rim: [number, number][] = [];
    for (let k = 0; k < 40; k++) rim.push([cy + Math.round(dsin((k * Math.PI) / 20) * R), cz + Math.round(dcos((k * Math.PI) / 20) * R)]);
    for (let k = 0; k < 40; k++) {
      const [y0, z0] = rim[k];
      const [y1, z1] = rim[(k + 1) % 40];
      b.line(cx, y0, z0, cx, y1, z1, M('paint_white'), 2);
    }
    for (let k = 0; k < 40; k += 5) {
      const [y0, z0] = rim[k];
      b.line(cx, cy, cz, cx, y0, z0, M('paint_white'), 1);
      b.line(cx + 3, cy, cz, cx + 3, y0, z0, M('paint_white'), 1);
      // Gondola hanging from the rim point.
      b.box(cx - 3, y0 - 10, z0 - 4, cx + 6, y0 - 2, z0 + 4, FLAGS[(k / 5) % FLAGS.length]);
      b.box(cx - 3, y0 - 3, z0 - 4, cx + 6, y0 - 2, z0 + 4, M('paint_white'));
    }
    b.box(cx - 3, cy - 4, cz - 4, cx + 6, cy + 4, cz + 4, M('chrome'));
    for (const dx of [-5, 8]) {
      b.line(cx + dx, cy, cz, cx + dx, G, cz - 34, M('paint_pink'), 3);
      b.line(cx + dx, cy, cz, cx + dx, G, cz + 34, M('paint_pink'), 3);
    }
    b.box(cx - 8, G, cz - 38, cx + 12, G + 2, cz + 38, M('concrete_2'));
  }

  // Metadata ----------------------------------------------------------------------------------------------------------------------------------
  const gy = G / 10;
  const meta = emptyMeta(gy, wx(33.0), wz(37.0));
  const mid: [number, number] = [wx(30), wz(35)];
  // Team 0 holds the east lane (north-east); team 1 the south-west yard.
  const t0: [number, number][] = [[44.6, 13.4], [46.6, 13.4], [48.6, 13.4], [50.6, 13.4], [45.2, 16.2], [47.2, 16.2], [49.2, 16.2], [51.2, 16.2]];
  const t1: [number, number][] = [[5.4, 58.6], [7.4, 58.6], [9.4, 58.6], [11.4, 58.6], [6.0, 60.8], [8.0, 60.8], [10.0, 60.8], [12.0, 60.8]];
  for (const [x, z] of t0) meta.spawns.push({ team: 0, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  for (const [x, z] of t1) meta.spawns.push({ team: 1, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  const ffa: [number, number][] = [
    [18.0, 33.0], [33.0, 31.5], [14.0, 35.0], [37.0, 37.0], [44.0, 38.5], [53.0, 38.5], [31.0, 39.5], [31.5, 50.0], [17.0, 61.0], [20.0, 57.0], [47.0, 22.5],
    [51.5, 24.0], [43.5, 11.0], [14.0, 29.0], [27.5, 29.0], [36.0, 14.0],
  ];
  for (const [x, z] of ffa) meta.spawns.push({ team: -1, x: wx(x), y: gy, z: wz(z), yaw: yawToward(wx(x), wz(z), mid[0], mid[1]) });
  meta.points.push({ id: 'A', name: 'West Street', x: wx(15.5), y: gy, z: wz(33.0), r: 3.2 });
  meta.points.push({ id: 'B', name: 'Fountain Plaza', x: wx(plazaC[0]), y: gy, z: wz(36.6), r: 3.0 });
  meta.points.push({ id: 'C', name: 'Arcade Lane', x: wx(51.0), y: gy, z: wz(37.8), r: 3.2 });
  meta.flags.push({ team: 0, x: wx(48.6), y: gy, z: wz(21.0) });
  meta.flags.push({ team: 1, x: wx(12.0), y: gy, z: wz(63.2) });
  const picks: [string, number, number][] = [
    ['health', 20.5, 35.5], ['health', 46.5, 23.5], ['health', 15.0, 62.0], ['health', 51.5, 39.5], ['health', 31.0, 28.5], ['health', 31.0, 51.5], ['armor', 17.0, 30.5],
    ['armor', 48.0, 35.5],
  ];
  for (const [kind, x, z] of picks) meta.pickups.push({ kind: kind as 'health' | 'armor', x: wx(x), y: b.standAt(wx(x), wz(z), 4.5), z: wz(z) });

  // Keep the voxels around every spawn clear and make sure there is floor under it.
  for (const s of meta.spawns) {
    const x = Math.floor(s.x * 10);
    const z = Math.floor(s.z * 10);
    const y = Math.round(s.y * 10);
    b.clear(x - 3, y, z - 3, x + 4, y + 18, z + 4);
    if (b.w.get(x, y - 1, z) === AIR) b.set(x, y - 1, z, M('stone_1'));
  }
  void ceilingLight;
  // Nothing that moves can leave the boundary: an invisible wall at every height along it (the boundary wall is the visible one).
  regionPlay(b, region, OX, OZ, BUILDINGS);
  return meta;
}

export const seasideArcade: MapDef = {
  id: 'seaside_arcade',
  name: 'Seaside Arcade',
  description: 'A sunny seaside town: shops, an arcade hall and a fountain plaza, with a Ferris wheel over the sea wall.',
  size: [SX, SY, SZ],
  modes: ['tdm', 'ffa', 'dom', 'ctf', 'sandbox', 'practice'],
  players: [4, 12],
  seed: 5103,
  version: 2,
  theme: {
    skyTop: 0x4e9cf4,
    skyHorizon: 0xd6f0ff,
    fog: 0xc8e8ff,
    fogNear: 55,
    fogFar: 180,
    sun: [0.4, 0.78, 0.42],
    sunColor: 0xfff6e0,
    sunIntensity: 1.35,
    ambient: 0xf0f6ff,
    ambientIntensity: 1.1,
    outside: 0x3a92d8,
    music: 'town',
  },
  build,
};
